import { api } from '../api/client';

class ProjectManager {
  constructor() {
    this.projects = [];
    this.sprints = [];
    this.projectTasks = [];
    this.sections = [];
  }

  // === PROJECT METHODS ===
  loadProjects() { return []; }
  saveProjects() { }

  normalizeProject(project = {}) {
    return {
      id: Number(project.id),
      name: project.name || '',
      color: project.color || '#D7C8F6',
      status: project.status || 'active',
      createdDate: project.createdAt
        ? new Date(project.createdAt).toISOString().split('T')[0]
        : (project.createdDate || new Date().toISOString().split('T')[0]),
      sprints: Array.isArray(project.sprints) ? project.sprints : [],
      client: project.client || '',
      team: Array.isArray(project.team) ? project.team : [],
      deadline: project.deadline || '',
      description: project.description || '',
      budget: project.budget ?? null,
      customFields: Array.isArray(project.customFields) ? project.customFields : [],
      ...project,
    };
  }

  async refreshProjects() {
    const res = await api('/api/projects');
    if (!res?.ok) return this.projects;

    const data = await res.json();
    const existingById = new Map((this.projects || []).map(p => [Number(p.id), p]));
    const fromApi = Array.isArray(data.projects) ? data.projects : [];

    this.projects = fromApi.map((project) => {
      const normalized = this.normalizeProject(project);
      const existing = existingById.get(normalized.id);
      return existing ? { ...existing, ...normalized } : normalized;
    });

    return this.projects;
  }

  getAllProjects() { return this.projects; }

  getProjectById(id) {
    const numericId = Number(id);
    return this.projects.find(p => Number(p.id) === numericId);
  }

  async createProject(projectData) {
    const payload = {
      name: projectData?.name,
      color: projectData?.color,
      status: projectData?.status || 'active',
      ...(projectData?.client !== undefined && { client: projectData.client }),
      ...(projectData?.clientEmail !== undefined && { clientEmail: projectData.clientEmail }),
      ...(projectData?.clientId !== undefined && { clientId: projectData.clientId }),
      ...(projectData?.budget !== undefined && { budget: projectData.budget }),
      ...(projectData?.deadline !== undefined && { deadline: projectData.deadline }),
    };

    const res = await api('/api/projects', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (!res?.ok) throw new Error('Failed to create project');

    const data = await res.json();
    const created = this.normalizeProject({ ...data.project, ...projectData });
    this.projects = [created, ...this.projects.filter(p => p.id !== created.id)];

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('aidey:projectsUpdated'));
    }

    return created;
  }

  async updateProject(id, updates) {
    const numericId = Number(id);
    const index = this.projects.findIndex(p => Number(p.id) === numericId);
    if (index === -1) return null;

    const payload = {
      ...(updates?.name !== undefined && { name: updates.name }),
      ...(updates?.color !== undefined && { color: updates.color }),
      ...(updates?.status !== undefined && { status: updates.status }),
      ...(updates?.client !== undefined && { client: updates.client }),
      ...(updates?.clientEmail !== undefined && { clientEmail: updates.clientEmail }),
      ...(updates?.clientId !== undefined && { clientId: updates.clientId }),
      ...(updates?.budget !== undefined && { budget: updates.budget }),
      ...(updates?.deadline !== undefined && { deadline: updates.deadline }),
    };

    const res = await api(`/api/projects/${numericId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });

    if (!res?.ok) throw new Error('Failed to update project');

    const data = await res.json();
    this.projects[index] = this.normalizeProject({ ...this.projects[index], ...updates, ...data.project });

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('aidey:projectsUpdated'));
    }

    return this.projects[index];
  }

  async deleteProject(id) {
    const numericId = Number(id);
    const res = await api(`/api/projects/${numericId}`, { method: 'DELETE' });
    if (!res?.ok) throw new Error('Failed to delete project');

    this.projects = this.projects.filter(p => Number(p.id) !== numericId);
    this.sprints = this.sprints.filter(s => Number(s.projectId) !== numericId);
    this.projectTasks = this.projectTasks.filter(t => Number(t.projectId) !== numericId);
    this.sections = this.sections.filter(s => Number(s.projectId) !== numericId);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('aidey:projectsUpdated'));
    }
  }

  getProject(id) { return this.getProjectById(id); }

  // === SUB-RESOURCE REFRESH ===
  async refreshProjectSubresources(projectId) {
    const numericId = Number(projectId);
    const [sprintsRes, tasksRes, sectionsRes] = await Promise.all([
      api(`/api/projects/${numericId}/sprints`),
      api(`/api/projects/${numericId}/tasks`),
      api(`/api/projects/${numericId}/sections`),
    ]);

    if (sprintsRes?.ok) {
      const data = await sprintsRes.json();
      this.sprints = [
        ...this.sprints.filter(s => Number(s.projectId) !== numericId),
        ...(data.sprints || []),
      ];
    }

    if (tasksRes?.ok) {
      const data = await tasksRes.json();
      this.projectTasks = [
        ...this.projectTasks.filter(t => Number(t.projectId) !== numericId),
        ...(data.tasks || []),
      ];
    }

    if (sectionsRes?.ok) {
      const data = await sectionsRes.json();
      this.sections = [
        ...this.sections.filter(s => Number(s.projectId) !== numericId),
        ...(data.sections || []),
      ];
    }
  }

  // === SPRINT METHODS ===
  getSprintsByProject(projectId) {
    return this.sprints.filter(s => Number(s.projectId) === Number(projectId));
  }

  async createSprint(sprintData) {
    const numericProjectId = Number(sprintData.projectId);
    const res = await api(`/api/projects/${numericProjectId}/sprints`, {
      method: 'POST',
      body: JSON.stringify({
        name: sprintData.name,
        startDate: sprintData.startDate,
        endDate: sprintData.endDate,
        goal: sprintData.goal,
        status: sprintData.status || 'planned',
        velocity: sprintData.velocity || 0,
      }),
    });

    if (!res?.ok) throw new Error('Failed to create sprint');

    const data = await res.json();
    this.sprints.push(data.sprint);
    return data.sprint;
  }

  updateSprint(id, updates) {
    const numericId = Number(id);
    const index = this.sprints.findIndex(s => Number(s.id) === numericId);
    if (index === -1) return null;

    this.sprints[index] = { ...this.sprints[index], ...updates };
    const sprint = this.sprints[index];

    api(`/api/projects/${sprint.projectId}/sprints/${numericId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }).catch(err => console.warn('Failed to update sprint:', err));

    return this.sprints[index];
  }

  deleteSprint(id) {
    const numericId = Number(id);
    const sprint = this.sprints.find(s => Number(s.id) === numericId);
    this.sprints = this.sprints.filter(s => Number(s.id) !== numericId);
    this.projectTasks = this.projectTasks.map(t =>
      Number(t.sprintId) === numericId ? { ...t, sprintId: null } : t
    );

    if (sprint) {
      api(`/api/projects/${sprint.projectId}/sprints/${numericId}`, { method: 'DELETE' })
        .catch(err => console.warn('Failed to delete sprint:', err));
    }
  }

  // === TASK METHODS ===
  getTasksByProject(projectId) {
    return this.projectTasks.filter(t => Number(t.projectId) === Number(projectId));
  }

  getTasksBySprint(sprintId) {
    return this.projectTasks.filter(t => Number(t.sprintId) === Number(sprintId));
  }

  getTasksByUser(userId) {
    return this.projectTasks.filter(t =>
      t.assignedTo && t.assignedTo.includes(userId)
    );
  }

  async createProjectTask(taskData) {
    const numericProjectId = Number(taskData.projectId);
    const res = await api(`/api/projects/${numericProjectId}/tasks`, {
      method: 'POST',
      body: JSON.stringify({
        title: taskData.title,
        ...(taskData.description !== undefined && { description: taskData.description }),
        ...(taskData.sprintId != null && { sprintId: taskData.sprintId }),
        ...(taskData.sectionId != null && { sectionId: taskData.sectionId }),
        ...(taskData.assignedTo !== undefined && { assignedTo: taskData.assignedTo }),
        ...(taskData.dueDate !== undefined && { dueDate: taskData.dueDate }),
        ...(taskData.priority !== undefined && { priority: taskData.priority }),
        ...(taskData.status !== undefined && { status: taskData.status }),
        ...(taskData.storyPoints !== undefined && { storyPoints: taskData.storyPoints }),
        ...(taskData.tags !== undefined && { tags: taskData.tags }),
        ...(taskData.parentTaskId != null && { parentTaskId: taskData.parentTaskId }),
      }),
    });

    if (!res?.ok) throw new Error('Failed to create task');

    const data = await res.json();
    this.projectTasks.push(data.task);
    return data.task;
  }

  updateProjectTask(id, updates) {
    const numericId = Number(id);
    const index = this.projectTasks.findIndex(t => Number(t.id) === numericId);
    if (index === -1) return null;

    this.projectTasks[index] = { ...this.projectTasks[index], ...updates };
    const task = this.projectTasks[index];

    api(`/api/projects/${task.projectId}/tasks/${numericId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }).catch(err => console.warn('Failed to update task:', err));

    return this.projectTasks[index];
  }

  deleteTask(id) {
    const numericId = Number(id);
    const task = this.projectTasks.find(t => Number(t.id) === numericId);
    const subtaskIds = this.projectTasks.filter(t => Number(t.parentTaskId) === numericId).map(t => t.id);
    this.projectTasks = this.projectTasks.filter(t => Number(t.id) !== numericId && Number(t.parentTaskId) !== numericId);
    this.projectTasks = this.projectTasks.map(t => ({
      ...t,
      dependencies: (t.dependencies || []).filter(depId => depId !== numericId && !subtaskIds.includes(depId)),
    }));

    if (task) {
      api(`/api/projects/${task.projectId}/tasks/${numericId}`, { method: 'DELETE' })
        .catch(err => console.warn('Failed to delete task:', err));
    }
  }

  getAllTasks() { return this.projectTasks; }
  getTaskById(id) { return this.projectTasks.find(t => Number(t.id) === Number(id)) || null; }

  // === SUBTASKS ===
  getSubtasks(parentTaskId) {
    return this.projectTasks.filter(t => Number(t.parentTaskId) === Number(parentTaskId));
  }

  async createSubtask(parentTaskId, subtaskData) {
    const parent = this.getTaskById(parentTaskId);
    if (!parent) return null;
    return this.createProjectTask({
      ...subtaskData,
      projectId: parent.projectId,
      sprintId: parent.sprintId,
      parentTaskId,
    });
  }

  // === SECTIONS ===
  getSectionsByProject(projectId) {
    return this.sections.filter(s => Number(s.projectId) === Number(projectId));
  }

  async createSection(projectId, name) {
    const numericProjectId = Number(projectId);
    const res = await api(`/api/projects/${numericProjectId}/sections`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });

    if (!res?.ok) throw new Error('Failed to create section');

    const data = await res.json();
    this.sections.push(data.section);
    return data.section;
  }

  deleteSection(id) {
    const numericId = Number(id);
    const section = this.sections.find(s => Number(s.id) === numericId);
    this.sections = this.sections.filter(s => Number(s.id) !== numericId);
    this.projectTasks = this.projectTasks.map(t =>
      Number(t.sectionId) === numericId ? { ...t, sectionId: null } : t
    );

    if (section) {
      api(`/api/projects/${section.projectId}/sections/${numericId}`, { method: 'DELETE' })
        .catch(err => console.warn('Failed to delete section:', err));
    }
  }

  // === TIME TRACKING ===
  logTime(taskId, userId, minutes, note = '') {
    const task = this.projectTasks.find(t => Number(t.id) === Number(taskId));
    if (!task) return null;

    const timeEntry = {
      userId,
      minutes,
      date: new Date().toISOString().split('T')[0],
      note,
      timestamp: Date.now()
    };

    task.timeTracked = [...(task.timeTracked || []), timeEntry];

    api(`/api/projects/${task.projectId}/tasks/${task.id}`, {
      method: 'PUT',
      body: JSON.stringify({ timeTracked: task.timeTracked }),
    }).catch(err => console.warn('Failed to log time:', err));

    return timeEntry;
  }

  getTotalTimeByTask(taskId) {
    const task = this.projectTasks.find(t => Number(t.id) === Number(taskId));
    return task ? (task.timeTracked || []).reduce((total, entry) => total + entry.minutes, 0) : 0;
  }

  getTotalTimeByProject(projectId) {
    const projectTasks = this.getTasksByProject(projectId);
    return projectTasks.reduce((total, task) =>
      total + (task.timeTracked || []).reduce((taskTotal, entry) => taskTotal + entry.minutes, 0), 0
    );
  }

  getTotalTimeByUser(userId) {
    return this.projectTasks.reduce((total, task) =>
      total + (task.timeTracked || [])
        .filter(entry => entry.userId === userId)
        .reduce((taskTotal, entry) => taskTotal + entry.minutes, 0), 0
    );
  }

  // === PRIORITY ESCALATION ===
  escalatePriorityIfDueSoon(task) {
    if (!task.dueDate) return task;
    const due = new Date(task.dueDate);
    const now = new Date();
    const diff = (due - now) / (1000 * 60 * 60 * 24);
    if (diff <= 2 && diff >= 0 && task.priority !== "High") {
      return { ...task, priority: "High" };
    }
    return task;
  }

  updateAllTaskPriorities() {
    let updated = false;
    this.projectTasks = this.projectTasks.map(task => {
      const escalated = this.escalatePriorityIfDueSoon(task);
      if (escalated.priority !== task.priority) updated = true;
      return escalated;
    });
    return updated;
  }

  // === COMMENTS ===
  addComment(taskId, userId, comment) {
    const task = this.projectTasks.find(t => Number(t.id) === Number(taskId));
    if (!task) return null;

    const newComment = {
      id: Date.now(),
      userId,
      comment,
      date: new Date().toISOString().split('T')[0],
      timestamp: Date.now()
    };

    task.comments = [...(task.comments || []), newComment];

    api(`/api/projects/${task.projectId}/tasks/${task.id}`, {
      method: 'PUT',
      body: JSON.stringify({ comments: task.comments }),
    }).catch(err => console.warn('Failed to save comment:', err));

    return newComment;
  }

  // === STATISTICS ===
  getProjectStats(projectId) {
    const tasks = this.getTasksByProject(projectId);
    const totalTime = this.getTotalTimeByProject(projectId);
    return {
      totalTasks: tasks.length,
      completedTasks: tasks.filter(t => t.status === 'completed').length,
      inProgressTasks: tasks.filter(t => t.status === 'in progress').length,
      todoTasks: tasks.filter(t => t.status === 'todo').length,
      totalTimeMinutes: totalTime,
      totalTimeHours: Math.round(totalTime / 60 * 10) / 10,
      highPriorityTasks: tasks.filter(t => t.priority === 'High').length
    };
  }
}

const projectManager = new ProjectManager();
export default projectManager;
